/**
 * Object of user roles
 */
export type RolesType =
  | 'ADMIN'
  | 'VENDOR'
  | 'VENDOR_STAFF'
  | 'VENDOR_OPERATIONS'
  | 'SCHEDULER'
  | 'INFLUENCER'
  | 'SOCIAL_MEDIA_MANAGER'
  | 'SOCIAL_MEDIA_OPS'
  | 'COMPLIANCE'
  | 'PRODUCT_DEVELOPMENT'
  | 'BUYER'
  | 'SPONSOR'
  | 'EXECUTIVE'
  | 'RECEIVER'
  | 'AP_ADMIN';

const Roles: { [role in RolesType]: role } = {
  ADMIN: 'ADMIN',
  VENDOR: 'VENDOR',
  VENDOR_STAFF: 'VENDOR_STAFF',
  VENDOR_OPERATIONS: 'VENDOR_OPERATIONS',
  SCHEDULER: 'SCHEDULER',
  INFLUENCER: 'INFLUENCER',
  SOCIAL_MEDIA_MANAGER: 'SOCIAL_MEDIA_MANAGER',
  SOCIAL_MEDIA_OPS: 'SOCIAL_MEDIA_OPS',
  COMPLIANCE: 'COMPLIANCE',
  PRODUCT_DEVELOPMENT: 'PRODUCT_DEVELOPMENT',
  BUYER: 'BUYER',
  SPONSOR: 'SPONSOR',
  EXECUTIVE: 'EXECUTIVE',
  RECEIVER: 'RECEIVER',
  AP_ADMIN: 'AP_ADMIN',
};

export default Roles;

export const UserRoleDisplay: Record<RolesType, string> = {
  ADMIN: 'Admin',
  VENDOR: 'Vendor',
  VENDOR_STAFF: 'Staff',
  VENDOR_OPERATIONS: 'Operations',
  SCHEDULER: 'Scheduler',
  INFLUENCER: 'Influencer',
  SOCIAL_MEDIA_MANAGER: 'SM Manager',
  SOCIAL_MEDIA_OPS: 'SM Ops',
  COMPLIANCE: 'Compliance',
  PRODUCT_DEVELOPMENT: 'Product Dev',
  BUYER: 'Buyer',
  SPONSOR: 'Sponsor',
  EXECUTIVE: 'Executive',
  RECEIVER: 'Receiver',
  AP_ADMIN: 'AP Admin',
};

export type UserRoleType = keyof typeof UserRoleDisplay;

export enum Actions {
  CREATE = 'CREATE',
  READ = 'READ',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
}

export type ActionType = `${Actions}:${string}`;

// Only base actions are listed here, do not add any custom actions here
const BaseActions: Record<Actions, `${Actions}:*`> = {
  [Actions.CREATE]: `${Actions.CREATE}:*`,
  [Actions.READ]: `${Actions.READ}:*`,
  [Actions.UPDATE]: `${Actions.UPDATE}:*`,
  [Actions.DELETE]: `${Actions.DELETE}:*`,
};

export type Pages =
  // TODO: remove this once chat is integrated with a project.
  | 'chat-hidden'
  | 'users/:id/chat-hidden'
  | 'dev-tools'
  | 'admin-console'
  | 'admin-console/vendor-init'
  | 'admin-console/public-files'
  | 'admin-console/feature-flags'
  | 'admin-console/permissions'
  | 'compliance-console'
  | 'compliance-factories'
  | 'social-media-console'
  | 'social-media-console/invite-tokens'
  | 'social-media-console/influencer-init'
  | 'users/:id/influencer-payee-information'
  | 'users/:id/influencer-user-info'
  | 'deliveries'
  | 'delivery-management'
  | 'users/:id/dashboard'
  | 'users/:id/requested-items'
  | 'users/:id/sample-requests'
  | 'users/:id/purchase-orders'
  | 'users/:id/purchase-orders/:purchaseOrderNumber/asn'
  | 'users/:id/deliveries'
  | 'users/:id/fn-catalog'
  | 'users/:id/printing-setup'
  | 'users/:id/help-center'
  | 'users/:id/announcement-logs'
  | 'users/:id/invoices'
  | 'users/:id/credits'
  | 'users/:id/payments'
  | 'users/:id/profile'
  | 'users/:id/factories'
  | 'users/:id/team-members'
  | 'vendor-applications'
  | 'vendor-applications/initiate-vendor-application'
  | 'vendor-applications/:vendorId/vendor-application-onboarding'
  | 'users/:id/sell-to-us'
  | 'users/:id/sample-tags'
  | 'users/:id/sell-to-us/:itemId'
  | 'users/:id/new-item-requests';

type PagePermissionsType = {
  [page in Pages]: Partial<Record<RolesType, ActionType[] | '*'>>;
};

export const PagePermissions: PagePermissionsType = {
  // TODO: remove this once chat is integrated with a project.
  'chat-hidden': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
  },
  'users/:id/chat-hidden': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.VENDOR]: '*',
    [Roles.VENDOR_STAFF]: '*',
  },
  'dev-tools': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
  },
  'admin-console': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.BUYER]: [BaseActions.READ],
    [Roles.PRODUCT_DEVELOPMENT]: [BaseActions.READ],
    [Roles.SPONSOR]: [BaseActions.READ],
    [Roles.EXECUTIVE]: [BaseActions.READ],
    [Roles.SCHEDULER]: [BaseActions.READ],
  },
  'admin-console/vendor-init': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.BUYER]: [BaseActions.CREATE, BaseActions.READ, BaseActions.UPDATE],
    [Roles.PRODUCT_DEVELOPMENT]: [
      BaseActions.CREATE,
      BaseActions.READ,
      BaseActions.UPDATE,
    ],
    [Roles.SPONSOR]: [BaseActions.CREATE, BaseActions.READ, BaseActions.UPDATE],
    [Roles.EXECUTIVE]: [BaseActions.READ],
    [Roles.SCHEDULER]: [BaseActions.READ],
  },
  'admin-console/public-files': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.COMPLIANCE]: [BaseActions.READ],
    [Roles.BUYER]: [BaseActions.READ],
    [Roles.PRODUCT_DEVELOPMENT]: [BaseActions.READ],
    [Roles.SPONSOR]: [BaseActions.READ],
    [Roles.EXECUTIVE]: [BaseActions.READ],
    [Roles.RECEIVER]: [BaseActions.READ],
    [Roles.SCHEDULER]: [BaseActions.READ],
  },
  'admin-console/feature-flags': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
  },
  'admin-console/permissions': {
    [Roles.ADMIN]: [BaseActions.READ],
    [Roles.AP_ADMIN]: [BaseActions.READ],
    [Roles.COMPLIANCE]: [BaseActions.READ],
    [Roles.BUYER]: [BaseActions.READ],
    [Roles.PRODUCT_DEVELOPMENT]: [BaseActions.READ],
    [Roles.SPONSOR]: [BaseActions.READ],
    [Roles.EXECUTIVE]: [BaseActions.READ],
    [Roles.SCHEDULER]: [BaseActions.READ],
  },
  'compliance-console': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.COMPLIANCE]: [BaseActions.READ, BaseActions.UPDATE],
  },
  'compliance-factories': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.COMPLIANCE]: [BaseActions.READ, BaseActions.UPDATE],
  },
  'social-media-console': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.SOCIAL_MEDIA_MANAGER]: [
      BaseActions.CREATE,
      BaseActions.READ,
      BaseActions.UPDATE,
      `${BaseActions.UPDATE}:ARCHIVE`,
    ],
    [Roles.SOCIAL_MEDIA_OPS]: [
      BaseActions.READ,
      `${BaseActions.UPDATE}:ARCHIVE`,
    ],
    [Roles.EXECUTIVE]: [BaseActions.READ],
  },
  'social-media-console/invite-tokens': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.SOCIAL_MEDIA_MANAGER]: [
      BaseActions.CREATE,
      BaseActions.READ,
      BaseActions.UPDATE,
    ],
  },
  'social-media-console/influencer-init': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.SOCIAL_MEDIA_MANAGER]: [
      BaseActions.CREATE,
      BaseActions.READ,
      BaseActions.UPDATE,
    ],
  },
  'users/:id/influencer-payee-information': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.SOCIAL_MEDIA_MANAGER]: [BaseActions.READ],
    [Roles.SOCIAL_MEDIA_OPS]: [BaseActions.READ],
    [Roles.INFLUENCER]: [BaseActions.READ, BaseActions.UPDATE],
  },
  'users/:id/influencer-user-info': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.SOCIAL_MEDIA_MANAGER]: [BaseActions.READ, BaseActions.UPDATE],
    [Roles.SOCIAL_MEDIA_OPS]: [BaseActions.READ],
    [Roles.INFLUENCER]: [BaseActions.READ, BaseActions.UPDATE],
  },
  deliveries: {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.COMPLIANCE]: [BaseActions.READ],
    [Roles.BUYER]: [BaseActions.READ],
    [Roles.PRODUCT_DEVELOPMENT]: [BaseActions.READ],
    [Roles.SPONSOR]: [BaseActions.READ],
    [Roles.EXECUTIVE]: [BaseActions.READ],
    [Roles.SCHEDULER]: '*',
    [Roles.RECEIVER]: [BaseActions.READ, BaseActions.UPDATE],
  },
  'delivery-management': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.SCHEDULER]: '*',
  },
  'users/:id/dashboard': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.BUYER]: [BaseActions.READ],
    [Roles.PRODUCT_DEVELOPMENT]: [BaseActions.READ],
    [Roles.SPONSOR]: [BaseActions.READ],
    [Roles.EXECUTIVE]: [BaseActions.READ],
    [Roles.VENDOR]: [BaseActions.READ, BaseActions.UPDATE],
  },
  'users/:id/requested-items': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.BUYER]: [BaseActions.READ],
    [Roles.PRODUCT_DEVELOPMENT]: [BaseActions.READ],
    [Roles.SPONSOR]: [BaseActions.READ],
    [Roles.EXECUTIVE]: [BaseActions.READ],
    [Roles.VENDOR]: [BaseActions.CREATE, BaseActions.READ, BaseActions.UPDATE],
    [Roles.VENDOR_STAFF]: [
      BaseActions.CREATE,
      BaseActions.READ,
      BaseActions.UPDATE,
    ],
  },
  'users/:id/sample-requests': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.BUYER]: [BaseActions.READ],
    [Roles.PRODUCT_DEVELOPMENT]: [BaseActions.READ],
    [Roles.SPONSOR]: [BaseActions.READ],
    [Roles.EXECUTIVE]: [BaseActions.READ],
    [Roles.VENDOR]: [BaseActions.CREATE, BaseActions.READ, BaseActions.UPDATE],
    [Roles.VENDOR_STAFF]: [
      BaseActions.CREATE,
      BaseActions.READ,
      BaseActions.UPDATE,
    ],
  },
  'users/:id/purchase-orders': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.COMPLIANCE]: [
      BaseActions.READ,
      `${Actions.UPDATE}:PRINT_WITHOUT_VENDOR_ACCEPTANCE`,
    ],
    [Roles.BUYER]: [BaseActions.READ],
    [Roles.PRODUCT_DEVELOPMENT]: [
      BaseActions.READ,
      `${Actions.UPDATE}:PRINT_WITHOUT_VENDOR_ACCEPTANCE`,
    ],
    [Roles.SPONSOR]: [BaseActions.READ],
    [Roles.EXECUTIVE]: [BaseActions.READ],
    [Roles.VENDOR]: [BaseActions.READ, BaseActions.UPDATE],
    [Roles.VENDOR_STAFF]: [BaseActions.READ, BaseActions.UPDATE],
    [Roles.VENDOR_OPERATIONS]: [`${Actions.UPDATE}:PRINT`], // operations should never have BaseActions.READ or VIEW_COST
    [Roles.SCHEDULER]: [
      BaseActions.READ,
      `${Actions.UPDATE}:PRINT_WITHOUT_VENDOR_ACCEPTANCE`,
    ],
  },
  'users/:id/purchase-orders/:purchaseOrderNumber/asn': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.VENDOR]: [BaseActions.READ, BaseActions.UPDATE],
    [Roles.VENDOR_STAFF]: [BaseActions.READ, BaseActions.UPDATE],
    [Roles.VENDOR_OPERATIONS]: [BaseActions.READ, BaseActions.UPDATE],
    [Roles.COMPLIANCE]: [BaseActions.READ, BaseActions.UPDATE],
    [Roles.SCHEDULER]: [BaseActions.READ, BaseActions.UPDATE],
  },
  'users/:id/deliveries': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.COMPLIANCE]: [BaseActions.READ],
    [Roles.BUYER]: [BaseActions.READ],
    [Roles.PRODUCT_DEVELOPMENT]: [BaseActions.READ],
    [Roles.SPONSOR]: [BaseActions.READ],
    [Roles.EXECUTIVE]: [BaseActions.READ],
    [Roles.VENDOR]: [BaseActions.READ, BaseActions.CREATE, BaseActions.UPDATE],
    [Roles.VENDOR_STAFF]: [
      BaseActions.READ,
      BaseActions.CREATE,
      BaseActions.UPDATE,
    ],
    [Roles.SCHEDULER]: [BaseActions.READ],
  },
  'users/:id/fn-catalog': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.COMPLIANCE]: [BaseActions.READ, BaseActions.UPDATE],
    [Roles.BUYER]: [BaseActions.READ],
    [Roles.PRODUCT_DEVELOPMENT]: [BaseActions.READ],
    [Roles.SPONSOR]: [BaseActions.READ],
    [Roles.EXECUTIVE]: [BaseActions.READ],
    [Roles.VENDOR]: [BaseActions.READ, BaseActions.UPDATE],
    [Roles.VENDOR_STAFF]: [BaseActions.READ, BaseActions.UPDATE],
    [Roles.SCHEDULER]: [BaseActions.READ],
  },
  'users/:id/printing-setup': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.COMPLIANCE]: [BaseActions.READ, BaseActions.UPDATE],
    [Roles.VENDOR]: [BaseActions.READ, BaseActions.UPDATE],
    [Roles.VENDOR_STAFF]: [BaseActions.READ, BaseActions.UPDATE],
    [Roles.VENDOR_OPERATIONS]: [BaseActions.READ, BaseActions.UPDATE],
  },
  'users/:id/invoices': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.SOCIAL_MEDIA_MANAGER]: [BaseActions.READ],
    [Roles.SOCIAL_MEDIA_OPS]: [BaseActions.READ],
    [Roles.INFLUENCER]: [BaseActions.READ, BaseActions.UPDATE],
    [Roles.EXECUTIVE]: [BaseActions.READ],
    [Roles.VENDOR]: [BaseActions.READ, BaseActions.UPDATE],
  },
  'users/:id/credits': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.SOCIAL_MEDIA_MANAGER]: [BaseActions.READ],
    [Roles.SOCIAL_MEDIA_OPS]: [BaseActions.READ],
    [Roles.INFLUENCER]: [BaseActions.READ, BaseActions.UPDATE],
    [Roles.EXECUTIVE]: [BaseActions.READ],
    [Roles.VENDOR]: [BaseActions.READ, BaseActions.UPDATE],
  },
  'users/:id/payments': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.SOCIAL_MEDIA_MANAGER]: [BaseActions.READ, BaseActions.UPDATE],
    [Roles.SOCIAL_MEDIA_OPS]: [BaseActions.READ, BaseActions.UPDATE],
    [Roles.INFLUENCER]: [BaseActions.READ, BaseActions.UPDATE],
    [Roles.EXECUTIVE]: [BaseActions.READ],
    [Roles.VENDOR]: [BaseActions.READ, BaseActions.UPDATE],
  },
  'users/:id/profile': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.COMPLIANCE]: [BaseActions.READ],
    [Roles.VENDOR]: '*',
  },
  'users/:id/team-members': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.VENDOR]: '*',
    [Roles.VENDOR_STAFF]: [BaseActions.READ],
  },
  'users/:id/factories': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.COMPLIANCE]: [
      BaseActions.READ,
      `${BaseActions.UPDATE}:ACCEPT_REJECT`,
    ],
    [Roles.BUYER]: [BaseActions.READ],
    [Roles.PRODUCT_DEVELOPMENT]: [BaseActions.READ],
    [Roles.SPONSOR]: [BaseActions.READ],
    [Roles.EXECUTIVE]: [BaseActions.READ],
    [Roles.VENDOR]: [
      BaseActions.CREATE,
      BaseActions.READ,
      `${Actions.UPDATE}:FACTORY`,
    ],
  },
  'users/:id/help-center': {
    [Roles.ADMIN]: [BaseActions.READ],
    [Roles.AP_ADMIN]: [BaseActions.READ],
    [Roles.VENDOR]: [BaseActions.READ],
    [Roles.VENDOR_STAFF]: [BaseActions.READ],
    [Roles.VENDOR_OPERATIONS]: [BaseActions.READ],
    [Roles.SCHEDULER]: [BaseActions.READ],
    [Roles.COMPLIANCE]: [BaseActions.READ],
    [Roles.PRODUCT_DEVELOPMENT]: [BaseActions.READ],
    [Roles.BUYER]: [BaseActions.READ],
    [Roles.SPONSOR]: [BaseActions.READ],
    [Roles.EXECUTIVE]: [BaseActions.READ],
  },
  'users/:id/sell-to-us': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.VENDOR]: '*',
    [Roles.VENDOR_STAFF]: [BaseActions.READ],
    [Roles.VENDOR_OPERATIONS]: [BaseActions.READ],
  },
  'users/:id/sell-to-us/:itemId': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.VENDOR]: '*',
    [Roles.VENDOR_STAFF]: [BaseActions.READ],
    [Roles.VENDOR_OPERATIONS]: [BaseActions.READ],
  },
  'users/:id/sample-tags': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.VENDOR]: '*',
    [Roles.VENDOR_STAFF]: [
      BaseActions.CREATE,
      BaseActions.READ,
      BaseActions.UPDATE,
    ],
    [Roles.VENDOR_OPERATIONS]: [
      BaseActions.CREATE,
      BaseActions.READ,
      BaseActions.UPDATE,
    ],
  },
  'users/:id/announcement-logs': {
    [Roles.ADMIN]: [BaseActions.READ],
    [Roles.AP_ADMIN]: [BaseActions.READ],
    [Roles.VENDOR]: [BaseActions.READ],
    [Roles.VENDOR_STAFF]: [BaseActions.READ],
    [Roles.VENDOR_OPERATIONS]: [BaseActions.READ],
  },
  'vendor-applications': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.BUYER]: [BaseActions.READ],
    [Roles.PRODUCT_DEVELOPMENT]: [BaseActions.READ],
    [Roles.SPONSOR]: [BaseActions.READ, BaseActions.UPDATE],
    [Roles.EXECUTIVE]: [BaseActions.READ, BaseActions.UPDATE],
  },
  'vendor-applications/initiate-vendor-application': {
    [Roles.ADMIN]: '*',
    [Roles.BUYER]: [BaseActions.CREATE, BaseActions.READ, BaseActions.UPDATE],
    [Roles.PRODUCT_DEVELOPMENT]: [
      BaseActions.CREATE,
      BaseActions.READ,
      BaseActions.UPDATE,
    ],
  },
  'vendor-applications/:vendorId/vendor-application-onboarding': {
    [Roles.ADMIN]: '*',
    [Roles.BUYER]: [BaseActions.READ, BaseActions.UPDATE],
    [Roles.PRODUCT_DEVELOPMENT]: [BaseActions.READ, BaseActions.UPDATE],
    [Roles.SPONSOR]: [BaseActions.READ, BaseActions.UPDATE],
    [Roles.EXECUTIVE]: [BaseActions.READ, BaseActions.UPDATE],
    [Roles.VENDOR]: [BaseActions.READ, BaseActions.UPDATE],
  },
  'users/:id/new-item-requests': {
    [Roles.ADMIN]: '*',
    [Roles.AP_ADMIN]: '*',
    [Roles.BUYER]: [BaseActions.READ],
    [Roles.PRODUCT_DEVELOPMENT]: [BaseActions.READ],
    [Roles.SPONSOR]: [BaseActions.READ],
    [Roles.EXECUTIVE]: [BaseActions.READ],
    [Roles.VENDOR]: [BaseActions.CREATE, BaseActions.READ, BaseActions.UPDATE],
    [Roles.VENDOR_STAFF]: [
      BaseActions.CREATE,
      BaseActions.READ,
      BaseActions.UPDATE,
    ],
  },
};

export const getAllowedRoleNames = (page: Pages): RolesType[] => {
  if (!PagePermissions[page]) return [];
  return Object.keys(PagePermissions[page]) as RolesType[];
};

export const hasPermissionTo = (
  page: Pages,
  role: RolesType,
  actionType: Actions,
  actionName: string,
): boolean => {
  const actions = PagePermissions[page]?.[role];
  if (!actions) return false;
  if (actions === '*') {
    return true;
  }
  const actionNameFound = actions
    .filter((action) => action.startsWith(actionType))
    .map((action) => action.split(':')[1])
    .find((action) => action === '*' || action === actionName);

  return !!actionNameFound;
};

export const hasAccessTo = (page: Pages, role: RolesType): boolean =>
  !!PagePermissions[page]?.[role];
